import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';

import { Observable, Subject, takeUntil } from 'rxjs';

import { KeyValue, ObjectBrowserFilter, ObjectBrowserFilterType } from '@mirage-projects/cw-models';

import { ObjectBrowserFilterService } from './filter.service';

/**
 * ObjectBrowser filter
 * This component manages the objectbrowser filtering (update, reset, )
 */
@Component({
    selector: 'cw-object-browser-filter',
    templateUrl: 'filter.component.html',
    styleUrls: ['./filter.component.scss'],
    standalone: false,
})
export class ObjectBrowserFilterComponent implements OnInit, OnDestroy, OnChanges {
    /** Array of filters to show */
    @Input() filters: ObjectBrowserFilter[] = [];
    /** Observable to let parent component makes a filter reset*/
    @Input() reset!: Observable<void>;
    /** Flag to override each filter disable state and disable all of them */
    @Input() areFiltersDisabled = false;

    /** Emits when any value of the elements composing the filter changes */
    @Output() filterChange = new EventEmitter<Map<string, string>>();

    onDestroy$: Subject<void> = new Subject();
    loading = false;
    isFilterReset = true;
    autocompleteDisabled = false;

    constructor(private filterService: ObjectBrowserFilterService) {}

    ngOnInit(): void {
        if (this.reset) {
            this.reset.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.resetFilters());
        }
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    ngOnChanges(): void {
        this.updateFilterResetState();
    }

    isVisible(item: ObjectBrowserFilter): boolean {
        return this.filterService.isVisible(item);
    }

    isFilterDisabled(itemDisabled: boolean | undefined): boolean {
        return this.areFiltersDisabled || (itemDisabled ?? false);
    }

    isFilterDisabledAttr(itemDisabled: boolean | undefined): boolean | null {
        return this.areFiltersDisabled || (itemDisabled ?? false) || null;
    }

    searchFilter(e: Event | string, item: ObjectBrowserFilter): void {
        item.value = typeof e === 'string' ? e : (item.value = (e.target as any).value);

        if (item.value.length >= 3 || !item.value) {
            this.executeFilter();
        }

        this.updateFilterResetState();
    }

    autocompleteFilter(selectedValue: KeyValue | null, item: ObjectBrowserFilter): void {
        if (selectedValue?.value && item.fieldName) {
            item.value = selectedValue.value;
            item.dirtyField = selectedValue.key;
            const params = new Map<string, string>();
            params.set(item.fieldName, selectedValue.key);
            this.filterChange.emit(params);
        }
        this.updateFilterResetState();
    }

    toggleFilter(item: ObjectBrowserFilter): void {
        item.value = !item.value;
        this.autocompleteDisabled = item.value;
        if (item.value) {
            const autocomplete = this.filters.filter((filter) => filter.type === ObjectBrowserFilterType.AUTOCOMPLETE);
            this.filterService.resetFilters(autocomplete);
        }
        this.executeFilter();
        this.updateFilterResetState();
    }

    selectFilter(item: ObjectBrowserFilter): void {
        if (typeof item.onChange === 'function') {
            item.onChange(item);
        }

        this.executeFilter();
        this.updateFilterResetState();
    }

    /**
     * Emits a filter change when there are modifications
     */
    executeFilter(): void {
        const params: Map<string, string> = this.filterService.getFilterParams(this.filters);
        this.filterChange.emit(params);
    }
    /**
     * Reset filters to their default value
     */
    resetFilters(): void {
        this.autocompleteDisabled = false;
        this.filterService.resetFilters(this.filters);
        this.executeFilter();
        this.updateFilterResetState();
    }

    /**
     * Checks if the filter is of a defined type
     * @param item Filter
     * @param type filter type to compare
     */
    isItemType(item: ObjectBrowserFilter, type: ObjectBrowserFilterType): boolean {
        return item.type === type;
    }

    private updateFilterResetState(): void {
        this.isFilterReset = this.filterService.isFilterReset(this.filters);
    }

    get objectBrowserFilterType(): typeof ObjectBrowserFilterType {
        return ObjectBrowserFilterType;
    }
}
